import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyGetKarsanStockVehiclesReportQuery } from '../../../api/endpoints/karsanStockVehiclesReport';
import { useLanguage } from '../../../contexts/LanguageContext';

const INITIAL_LAZY_PARAMS = { first: 0, rows: 10, page: 1 };

const FILTER_OPERATOR_CONTAINS = 1;
const FILTER_OPERATOR_EQUALS = 5;

const columnOperatorMap = {
    brand: FILTER_OPERATOR_EQUALS,
    model: FILTER_OPERATOR_EQUALS
};

const backendColumnMap = {
    brand: 'Brand',
    model: 'Model',
    maktx: 'Maktx',
    matnr: 'Matnr',
    sasiNo: 'SasiNo'
};

const buildFiltersFromGenericFilter = (filterValues) => {
    const filters = [];
    Object.entries(filterValues).forEach(([field, value]) => {
        if (value === null || value === undefined || value === '') return;
        const column = backendColumnMap[field];
        if (!column) return;
        const filterValue = String(value).trim();
        if (filterValue === '') return;
        filters.push({ column, operator: columnOperatorMap[field] ?? FILTER_OPERATOR_CONTAINS, value: filterValue });
    });
    return filters;
};

export default function useKarsanStockVehiclesReport({ enabled = true } = {}) {
    const { getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState(INITIAL_LAZY_PARAMS);
    const [activeFilters, setActiveFilters] = useState({});
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const [getReport, { data, isLoading: isFetching }] = useLazyGetKarsanStockVehiclesReportQuery();

    const queryParams = useMemo(() => {
        const filters = buildFiltersFromGenericFilter(activeFilters);
        const sorts = sortField && sortOrder !== 0 
        ? [{ column: backendColumnMap[sortField] || sortField, direction: sortOrder === 1 ? 1 : 2 }]
         : undefined;
        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : undefined,
            sorts
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    useEffect(() => {
        if (!enabled) return;
        getReport(queryParams);
    }, [queryParams, getReport, enabled]);

    const items = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;

    const handleApplyFilters = useCallback((filterValues) => {
        setActiveFilters(filterValues);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleResetFilters = useCallback(() => {
        setActiveFilters({});
        setSortField(null);
        setSortOrder(0);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleSortChange = useCallback((event) => {
        setSortField(event?.sortField || null);
        setSortOrder(event?.sortOrder || 0);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        lazyParams,
        activeFilters,
        handleApplyFilters,
        handleResetFilters,
        onPage,
        sortField,
        sortOrder,
        onSort: handleSortChange
    };
}
